import React from 'react';

import { Center, Divider, Icon, Skeleton, makeUclComponent } from '@rbilabs/universal-components';
import { StyleSheet } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import QRCode from 'components/qrcode';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { Box, BoxProps, TypographyProps, withTypography } from 'components/ui';
import { primitive, spacing } from 'styles/constants';

type ShortCodeContentProps = BoxProps & {
  newUI?: boolean;
};

type ShortCodeTextProps = TypographyProps & {
  newUI?: boolean;
};

type ShortCodeWidgetContainerProps = BoxProps & {
  isQrEnabled?: boolean;
};

const styles = StyleSheet.create({
  shortCodeContent: {
    backgroundColor: primitive.$crownColor,
    borderRadius: primitive.$borderRadius,
    marginBottom: spacing.$2,
    marginHorizontal: 'auto',
    minWidth: spacing.$64,
    paddingHorizontal: spacing.$6,
    width: 'auto',
  },
  shortCodeContainer: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'column',
    marginTop: spacing.$4,
  },
  errorMessage: {
    margin: spacing.$0,
  },
  qrCodeContainer: {
    alignContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    backgroundColor: primitive.$crownColor,
    flexDirection: 'column',
    minHeight: spacing.$64,
    width: '100%',
  },
  shortCodeWidgetContainer: {
    alignSelf: 'center',
    backgroundColor: primitive.$crownColor,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    flexDirection: 'column',
    width: '100%',
  },
  shortCodeWidgetContainerWithoutQr: {
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  },
  shortCodeWidgetContainerRedesign: {
    alignSelf: 'center',
    backgroundColor: primitive.$crownColor,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    flex: 1,
    flexDirection: 'column',
    width: '100%',
  },
  shortCodeRefreshBtnContainer: {
    alignSelf: 'center',
    flex: 1,
    paddingHorizontal: spacing.$10,
    paddingVertical: spacing.$7,
    width: spacing.$56,
  },
  qrCodeTitleContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  footer: {
    alignItems: 'center',
    backgroundColor: primitive.$whiteOpacity30,
    padding: spacing.$3,
    width: '100%',
  },
  termsContainer: {
    justifyContent: 'center',
    marginBottom: spacing.$3,
    marginTop: spacing.$2,
  },
});

export const ShortCodeContent = ({ newUI: _newUI, style, ...props }: ShortCodeContentProps) =>
  React.createElement(Box, { ...props, style: [styles.shortCodeContent, style] });

const ShortCodeTextBase = withTypography({
  fontFamily: 'heading',
  bold: true,
  color: primitive.$bbqBrown,
  fontSize: '6xl',
  textAlign: 'center',
  lineHeight: 'xs',
});

export const ShortCodeText = ({ newUI: _newUI, ...props }: ShortCodeTextProps) =>
  React.createElement(ShortCodeTextBase, props);

export const ShortCodeErrorContainer = Center.withConfig<{ newUI?: boolean }>(p => ({
  borderStyle: 'solid',
  borderWidth: '1',
  borderColor: primitive.$greyFive,
  paddingY: '$4',
  paddingX: '$7',
  marginX: 'auto',
  marginTop: '$6',
  marginBottom: '$8',
  borderRadius: primitive.$borderRadius,
  backgroundColor: p.newUI ? null : primitive.$bbqBrown,
  width: 'auto',
  minWidth: '$64',
  minHeight: '$24',
}));

export const StyledShortCodeErrorActionButton = makeUclComponent(ActionButton).withConfig<{
  newUI?: boolean;
}>(p => ({
  variant: p.newUI ? ActionButtonVariants.PRIMARY : ActionButtonVariants.INVERSE,
  marginTop: '$2',
  marginX: 'auto',
  marginBottom: '$0',
  fontSize: 'sm',
  paddingY: spacing.$2_5,
  paddingX: '$8',
}));

export const ShortCodeContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.shortCodeContainer, style] });

export const ShortCodeTitle = SanityBlockRenderer.withConfig({
  textProps: {
    color: primitive.$bbqBrown,
    lineHeight: 'lg',
    fontSize: 'lg',
    fontWeight: 'bold',
  },
  marginLeft: '$1',
});

export const ShortCodeSubtitle = SanityBlockRenderer.withConfig({
  textProps: { color: primitive.$bbqBrown, fontSize: 'sm', textAlign: 'center' },
});

export const ShortCodeTitleRedesign = SanityBlockRenderer.withConfig({
  textProps: {
    color: primitive.$bbqBrown,
    lineHeight: 'lg',
    fontSize: 'md',
  },
  marginLeft: '$1',
  marginBottom: '$4',
});

export const ShortCodeSubtitleRedesign = SanityBlockRenderer.withConfig({
  textProps: {
    color: primitive.$bbqBrown,
    lineHeight: 'lg',
    fontSize: 'md',
    fontWeight: 'bold',
    textAlign: 'center',
  },
});

export const StyledRefreshIcon = Icon.withConfig({
  variant: 'refresh',
  color: primitive.$bbqBrown,
});

export const ErrorMessage = withTypography({
  color: primitive.$mayoEggWhite,
  style: styles.errorMessage,
});

export const ErrorMessageTitle = withTypography({
  bold: true,
  color: primitive.$mayoEggWhite,
  style: styles.errorMessage,
});

export const QRCodeContainer = ({ newUI: _newUI, style, ...props }: ShortCodeContentProps) =>
  React.createElement(Box, { ...props, style: [styles.qrCodeContainer, style] });

export const ShortCodeWidgetContainer = ({
  isQrEnabled,
  style,
  ...props
}: ShortCodeWidgetContainerProps) =>
  React.createElement(Box, {
    ...props,
    style: [
      styles.shortCodeWidgetContainer,
      !isQrEnabled && styles.shortCodeWidgetContainerWithoutQr,
      style,
    ],
  });

export const ShortCodeWidgetContainerRedesign = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.shortCodeWidgetContainerRedesign, style],
  });

export const ShortCodeRefreshBtnContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.shortCodeRefreshBtnContainer, style] });

export const ShortCodeRefreshButton = makeUclComponent(ActionButton).withConfig<{
  isLoading?: boolean;
}>(p => ({
  width: 'full',
  variant: p.isLoading ? ActionButtonVariants.PRIMARY : ActionButtonVariants.OUTLINE,
  backgroundColor: primitive.$white,
}));

export const QRCodeTitleContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.qrCodeTitleContainer, style] });

export const QRCodeTitle = SanityBlockRenderer.withConfig({
  textProps: {
    color: primitive.$bbqBrown,
    lineHeight: 'lg',
    fontSize: 'lg',
    fontWeight: 'bold',
  },
  marginLeft: '$1',
});

export const QRCodeTitleRedesign = SanityBlockRenderer.withConfig({
  textProps: {
    color: primitive.$bbqBrown,
    lineHeight: 'lg',
    fontSize: 'md',
    fontWeight: 'bold',
  },
  marginLeft: '$1',
});

export const QRCodeSubtitle = SanityBlockRenderer.withConfig<{ newUI?: boolean }>(p => ({
  textProps: { color: primitive.$bbqBrown, fontSize: 'sm' },
  marginBottom: '$1',
}));

export const StyledQRCode = QRCode.withConfig({
  marginTop: '$2',
  marginBottom: '$8',
  width: '$40',
  height: '$40',
  borderRadius: 'sm',
});

export const StyledQRSkeleton = Skeleton.withConfig({
  size: '40',
  marginBottom: '$6',
  borderRadius: 'lg',
});

export const Footer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.footer, style] });

export const QrDivider = Divider.withConfig({
  width: '4/5',
  marginBottom: '$4',
  background: primitive.$blackOpacity20,
});

export const CodeHeaderText = SanityBlockRenderer.withConfig({
  textProps: {
    textAlign: 'center',
    fontFamily: 'heading',
    fontSize: '2xl',
    lineHeight: '2xl',
    marginBottom: 0,
  },
});

export const TermsContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.termsContainer, style] });

export const TermsText = SanityBlockRenderer.withConfig({
  textProps: {
    textAlign: 'center',
    fontSize: '2xs',
    lineHeight: 'lg',
    marginLeft: '$10',
    marginRight: '$10',
  },
});
